/**
 * Truncation limit for captured callback source. Injected code can embed call
 * or agency data, and this runs in a 911 console, so keep the excerpt short and
 * keep it out of indexed tags.
 */
const MAX_SOURCE_LENGTH = 200;

/** Upper bound on the attribute names read off `<html>`. */
const MAX_HTML_ATTRIBUTES = 20;

/** Truncation limit for the original error message carried into our message. */
const MAX_MESSAGE_LENGTH = 100;

const TIMER_APIS = ['setTimeout', 'setInterval'] as const;

type TimerApi = (typeof TIMER_APIS)[number];

type TimerCallback = (...args: unknown[]) => unknown;

type TimerScheduler = (
  handler: TimerHandler,
  timeout?: number,
  ...args: unknown[]
) => number;

type Reporter = (
  error: Error | string,
  extra: Record<string, unknown>,
  fingerprint?: string[],
  tags?: Record<string, string>,
) => void;

let isReporting = false;

function readProperty(target: object, key: string): unknown {
  return Reflect.get(target, key);
}

function isTimerCallback(value: unknown): value is TimerCallback {
  return typeof value === 'function';
}

function isTimerScheduler(value: unknown): value is TimerScheduler {
  return typeof value === 'function';
}

/**
 * True when the frame that threw has no script URL. V8 renders code compiled
 * from a string — `new Function`, `eval`, a host's script-evaluation API, or a
 * DevTools-protocol evaluation — as `<anonymous>` or `eval at`.
 *
 * The check matches those forms positively rather than excluding known URL
 * schemes, so anything served as a real script (including under a test runner,
 * where paths look nothing like production) is left alone.
 *
 * Both the frame filter and the patterns are V8 stack formats, so this always
 * returns false on SpiderMonkey and JavaScriptCore, whose frames read
 * `name@url:line:col` and whose string-compiled code renders as
 * `line 1 > Function`. Deliberate: the reported population is entirely
 * Chromium, and failing closed reads no source rather than guessing at another
 * engine's format. Absence of non-Chromium data downstream is a property of
 * this gate, not evidence that no such errors occur.
 */
export function isAnonymousSourceStack(stack: string | undefined): boolean {
  const lines = String(stack ?? '').split('\n');
  const topFrame = lines.find(line => /^\s*at\s/.test(line)) ?? '';
  return /<anonymous>:\d+:\d+|\(eval at /.test(topFrame);
}

/** Collapses a callback's source to a short, single-line excerpt. */
export function summarizeCallbackSource(callback: TimerCallback): string {
  try {
    return callback.toString().replace(/\s+/g, ' ').slice(0, MAX_SOURCE_LENGTH);
  } catch {
    // Proxies and cross-realm functions can throw on toString.
    return '<unavailable>';
  }
}

/**
 * FNV-1a in base 36. Short and stable, so distinct injected scripts stay in
 * distinct Sentry issues while the source excerpt itself stays unindexed.
 */
export function hashSource(source: string): string {
  let hash = 0x811c9dc5;
  for (const character of source) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

function describeDocument(): Record<string, unknown> {
  return {
    inlineScriptCount: document.querySelectorAll('script:not([src])').length,
    htmlAttributes: document.documentElement
      .getAttributeNames()
      .slice(0, MAX_HTML_ATTRIBUTES)
      .join(','),
  };
}

function reportAnonymousSource(
  report: Reporter,
  error: unknown,
  callback: TimerCallback,
  timerApi: TimerApi,
): void {
  if (isReporting) {
    return;
  }
  isReporting = true;
  try {
    const source = summarizeCallbackSource(callback);
    const hash = hashSource(source);
    const originalMessage =
      error instanceof Error ? error.message : String(error);
    // A distinct message, rather than the original error, keeps Sentry's dedupe
    // integration from treating this and the rethrown original as one event —
    // dedupe matches on message and stack trace.
    report(
      new Error(
        `Injected timer callback threw: ${originalMessage.slice(
          0,
          MAX_MESSAGE_LENGTH,
        )}`,
      ),
      {
        callbackSource: source,
        timerApi,
        originalStack: readErrorStack(error),
        ...describeDocument(),
      },
      ['injected-timer-callback', hash],
      { 'injected.source_hash': hash, 'injected.timer_api': timerApi },
    );
  } finally {
    isReporting = false;
  }
}

function wrapCallback(
  callback: TimerCallback,
  report: Reporter,
  timerApi: TimerApi,
): TimerCallback {
  return function diagnosedTimerCallback(this: unknown, ...args: unknown[]) {
    try {
      return callback.apply(this, args);
    } catch (error) {
      if (isAnonymousSourceStack(readErrorStack(error))) {
        reportAnonymousSource(report, error, callback, timerApi);
      }
      // Rethrown unchanged so Sentry's own BrowserApiErrors wrapper still
      // reports the error with its usual mechanism and grouping.
      throw error;
    }
  };
}

function readErrorStack(error: unknown): string | undefined {
  if (!(error instanceof Error)) {
    return undefined;
  }
  return typeof error.stack === 'string' ? error.stack : undefined;
}

function patchTimer(timerApi: TimerApi, report: Reporter): void {
  const candidate = readProperty(window, timerApi);
  if (!isTimerScheduler(candidate)) {
    return;
  }
  const original: TimerScheduler = candidate;

  function patchedTimer(
    handler: TimerHandler,
    timeout?: number,
    ...args: unknown[]
  ): number {
    // The string form compiles its own code and never reaches a callback we
    // could inspect, so pass it through untouched.
    const wrapped = isTimerCallback(handler)
      ? wrapCallback(handler, report, timerApi)
      : handler;
    return Reflect.apply(original, window, [wrapped, timeout, ...args]);
  }

  // Assigned through Reflect because the DOM lib types these as overloads that
  // a plain assignment cannot satisfy without a cast.
  Reflect.set(window, timerApi, patchedTimer);
}

/**
 * Records the source of timer callbacks that throw from string-compiled code,
 * so those errors can be told apart from our own.
 *
 * Sentry's `BrowserApiErrors` integration patches `window.setTimeout`, so every
 * page-context timer callback is wrapped and reported under our DSN — including
 * callbacks belonging to software we did not ship. Page-context injection comes
 * from an embedded browser host, an extension injecting into the main world,
 * WebDriver or DevTools-protocol automation, or a bookmarklet. Those errors
 * arrive with no first-party frame and a single `<anonymous>` location, which
 * leaves the only question that matters unanswerable: is this ours? Until it is
 * answered the error can be neither fixed nor safely filtered, because the
 * error shape alone (a `TypeError` in a timer) is one our own code could
 * produce, so a filter written against the message risks hiding real defects.
 *
 * A function compiled from a string still knows its own source, and that
 * excerpt is what settles the question. A selector or idiom belonging to our UI
 * means something is driving our interface and we broke it — ours to fix.
 * Anything else identifies the injector, which is what justifies filtering it
 * out and, if it is worth chasing, tells support what to ask the site about.
 * Callbacks served from a real script URL are never read.
 *
 * The error is rethrown, so this only adds a second, richer event — it does not
 * change which errors are reported or how the original is grouped.
 *
 * Install this **after** `Sentry.init`. This patch then delegates to Sentry's,
 * so at call time our wrapper runs inside Sentry's and still holds the caller's
 * original callback. Installed first, Sentry would wrap this patch instead: the
 * captured source would be Sentry's own wrapper, and the mechanism name Sentry
 * derives from the scheduler it wrapped would change on the existing issue.
 * @param report - Reports an exception with extra context, tags and a
 *   fingerprint (`captureExceptionWithScope`).
 */
export function installAnonymousTimerDiagnostics(report: Reporter): void {
  for (const timerApi of TIMER_APIS) {
    patchTimer(timerApi, report);
  }
}
